import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Clock, AlertCircle, CheckCircle, Send, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import MathWorkspace from '../components/MathWorkspace';

const ChapterTest = () => {
  const { subject, chapterId } = useParams();
  const navigate = useNavigate();
  const [testData, setTestData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(3600); // 60 minutes
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [currentSection, setCurrentSection] = useState('A');
  const startTime = React.useRef(Date.now());

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await apiService.getChapterTest(subject, chapterId);
        setTestData(res.data);
        startTime.current = Date.now();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load test');
        navigate(`/chapters/${subject}`);
      } finally { setLoading(false); }
    };
    fetchTest();
  }, [subject, chapterId, navigate]);

  useEffect(() => {
    if (!testData || result) return;
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { handleSubmit(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [testData, result]);

  const handleSubmit = useCallback(async () => {
    if (submitting || result) return;
    setSubmitting(true);
    const timeTaken = (Date.now() - startTime.current) / 60000;
    try {
      const res = await apiService.submitChapterTest({
        test_session_id: testData.test_session_id,
        answers,
        time_taken_minutes: Math.round(timeTaken * 10) / 10,
      });
      setResult(res.data);
      if (res.data.passed) toast.success('Congratulations! You passed!');
      else toast.error(`Score: ${res.data.percentage}% - Need 75% to pass`);
    } catch (e) {
      toast.error('Failed to submit test');
    } finally { setSubmitting(false); }
  }, [testData, answers, submitting, result]);

  const setAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));

  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  if (loading) return (
    <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#4D96FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-lg font-semibold text-[#64748B]">Preparing your test...</p>
      </div>
    </div>
  );

  // ============ RESULT SCREEN ============
  if (result) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] py-8" data-testid="test-result-page">
        <div className="max-w-4xl mx-auto px-4">
          <div className={`rounded-3xl p-10 text-center shadow-2xl mb-8 ${result.passed ? 'bg-gradient-to-br from-green-400 to-emerald-600' : 'bg-gradient-to-br from-orange-400 to-red-500'} text-white`}>
            <div className="text-7xl mb-4">{result.passed ? '🎉' : '💪'}</div>
            <h1 className="text-5xl font-black mb-2">{result.percentage}%</h1>
            <p className="text-2xl font-bold mb-2">{result.earned_marks}/{result.total_marks} marks</p>
            <p className="text-xl opacity-90 mb-4">{result.feedback}</p>
            <p className="text-sm opacity-75">Time taken: {result.time_taken_minutes} minutes | +{result.xp_gained} XP</p>
            {result.next_chapter_unlocked && (
              <div className="mt-4 bg-white/20 rounded-2xl p-4 inline-block">
                <p className="font-bold text-lg">Next Chapter Unlocked!</p>
              </div>
            )}
          </div>

          {/* Detailed Results */}
          <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-6">
            <h2 className="text-2xl font-black text-[#1E293B] mb-6">Detailed Results</h2>
            <div className="space-y-3">
              {result.results.map((r, i) => (
                <div key={i} className={`p-4 rounded-2xl border-2 ${r.correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-[#64748B] font-semibold">Q{i + 1}</p>
                      <p className={`font-bold ${r.correct ? 'text-green-700' : 'text-red-700'}`}>
                        {r.correct ? <CheckCircle className="w-4 h-4 inline mr-1" /> : <AlertCircle className="w-4 h-4 inline mr-1" />}
                        {r.marks_awarded}/{r.max_marks} marks
                      </p>
                    </div>
                    {!r.correct && r.correct_answer && (
                      <p className="text-sm text-[#64748B]">Correct: <strong className="text-green-700">{String(r.correct_answer).substring(0, 60)}</strong></p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button onClick={() => navigate(`/chapters/${subject}`)} data-testid="back-to-chapters-btn"
              className="px-8 py-4 bg-[#4D96FF] text-white rounded-2xl font-bold border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all">
              Back to Chapters
            </button>
            {!result.passed && (
              <button onClick={() => window.location.reload()} data-testid="retry-test-btn"
                className="px-8 py-4 bg-[#FFC03D] text-white rounded-2xl font-bold border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 transition-all">
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ============ TEST SCREEN ============
  const questions = testData?.questions || [];
  const sections = { A: [], B: [], C: [], D: [] };
  questions.forEach(q => { if (sections[q.section]) sections[q.section].push(q); });
  const currentQuestions = sections[currentSection] || [];
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="chapter-test-page">
      {/* Timer Header */}
      <div className="sticky top-0 z-50 bg-white border-b-2 border-slate-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#1E293B]">{testData?.chapter?.name}</h2>
              <p className="text-sm text-[#64748B]">{answeredCount}/{questions.length} answered</p>
            </div>
            <div className={`flex items-center gap-2 px-5 py-2 rounded-2xl font-bold text-white ${timeLeft <= 300 ? 'bg-red-500 animate-pulse' : timeLeft <= 600 ? 'bg-orange-500' : 'bg-[#4D96FF]'}`}>
              <Clock className="w-5 h-5" />
              <span className="text-xl font-black">{formatTime(timeLeft)}</span>
            </div>
            <button onClick={handleSubmit} disabled={submitting} data-testid="submit-test-btn"
              className="px-6 py-2 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all flex items-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" />{submitting ? 'Submitting...' : 'Submit Test'}
            </button>
          </div>
          {/* Section Tabs */}
          <div className="flex gap-2 mt-3">
            {['A', 'B', 'C', 'D'].map(sec => (
              <button key={sec} onClick={() => setCurrentSection(sec)} data-testid={`section-tab-${sec}`}
                className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${currentSection === sec ? 'bg-[#4D96FF] text-white' : 'bg-slate-100 text-[#64748B] hover:bg-slate-200'}`}>
                Section {sec} ({sections[sec].length})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4 p-4 bg-blue-50 rounded-2xl border-2 border-blue-200">
          <p className="font-bold text-blue-700">
            {testData?.sections?.[currentSection]?.name} | {testData?.sections?.[currentSection]?.marks}
          </p>
        </div>
        <div className="space-y-6">
          {currentQuestions.map((q, idx) => (
            <QuestionRenderer key={q.id} question={q} index={idx} answer={answers[q.id]} setAnswer={(val) => setAnswer(q.id, val)} />
          ))}
        </div>
      </div>
      {subject === 'mathematics' && <MathWorkspace />}
    </div>
  );
};

const QuestionRenderer = ({ question, index, answer, setAnswer }) => {
  const q = question;

  return (
    <div className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-sm" data-testid={`question-${q.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <span className="bg-[#4D96FF] text-white w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0">{index + 1}</span>
        <div className="flex-1">
          <p className="font-bold text-[#1E293B] whitespace-pre-line">{q.question}</p>
          <span className="text-xs text-[#64748B] font-semibold mt-1 inline-block bg-slate-100 px-2 py-1 rounded">{q.type.replace('_', ' ')} | {q.marks} mark{q.marks > 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* MCQ */}
      {(q.type === 'MCQ' || q.type === 'ASSERTION_REASON' || q.type === 'CASE_BASED') && q.options && (
        <div className="space-y-2 ml-11">
          {q.options.map((opt, i) => (
            <button key={i} onClick={() => setAnswer(opt)}
              className={`w-full text-left p-4 rounded-xl border-2 font-medium transition-all ${answer === opt ? 'bg-[#D8E8FF] border-[#4D96FF] text-[#1E293B]' : 'bg-[#F4F8FB] border-slate-200 hover:border-[#4D96FF] text-[#1E293B]'}`}
              data-testid={`option-${q.id}-${i}`}>
              <span className="font-bold mr-2 text-[#64748B]">{String.fromCharCode(65 + i)}.</span>{opt}
            </button>
          ))}
        </div>
      )}

      {/* True/False */}
      {q.type === 'TRUE_FALSE' && (
        <div className="flex gap-4 ml-11">
          {['True', 'False'].map(opt => (
            <button key={opt} onClick={() => setAnswer(opt)}
              className={`flex-1 p-4 rounded-xl border-2 font-bold text-center transition-all ${answer === opt ? 'bg-[#D8E8FF] border-[#4D96FF]' : 'bg-[#F4F8FB] border-slate-200 hover:border-[#4D96FF]'}`}
              data-testid={`tf-${q.id}-${opt.toLowerCase()}`}>
              {opt}
            </button>
          ))}
        </div>
      )}

      {/* Fill in Blank */}
      {q.type === 'FILL_BLANK' && (
        <div className="ml-11">
          <input type="text" value={answer || ''} onChange={(e) => setAnswer(e.target.value)}
            className="w-full p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-xl focus:border-[#4D96FF] outline-none font-medium"
            placeholder="Type your answer..." data-testid={`fill-${q.id}`} />
        </div>
      )}

      {/* Short Answer */}
      {q.type === 'SHORT_ANSWER' && (
        <div className="ml-11">
          <textarea value={answer || ''} onChange={(e) => setAnswer(e.target.value)} rows={3}
            className="w-full p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-xl focus:border-[#4D96FF] outline-none font-medium resize-none"
            placeholder="Write your answer in 2-3 sentences..." data-testid={`short-${q.id}`} />
        </div>
      )}

      {/* Long Answer */}
      {q.type === 'LONG_ANSWER' && (
        <div className="ml-11">
          <textarea value={answer || ''} onChange={(e) => setAnswer(e.target.value)} rows={6}
            className="w-full p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-xl focus:border-[#4D96FF] outline-none font-medium resize-none"
            placeholder="Write a detailed answer with examples..." data-testid={`long-${q.id}`} />
        </div>
      )}

      {/* Match */}
      {q.type === 'MATCH' && q.left_column && (
        <div className="ml-11 space-y-3">
          {q.left_column.map((left, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="font-bold text-[#1E293B] w-1/3 p-3 bg-blue-50 rounded-xl">{left}</span>
              <span className="text-[#64748B]">=</span>
              <select value={(answer && answer[left]) || ''} onChange={(e) => setAnswer({ ...(answer || {}), [left]: e.target.value })}
                className="flex-1 p-3 bg-[#F4F8FB] border-2 border-slate-200 rounded-xl font-medium focus:border-[#4D96FF] outline-none"
                data-testid={`match-${q.id}-${i}`}>
                <option value="">Select match...</option>
                {q.right_column.map((right, j) => (
                  <option key={j} value={right}>{right}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChapterTest;
